import { ScrollToTop } from '@/app/ScrollToTop';
import { MainConfigContextProvider } from '@/app/mainConfig';
import { createRefineKeyResolver } from '@/app/refineKey';
import useI18nProvider from '@/app/useI18nProvider';
import { useCurrentUser } from '@/hooks/dataHelpers/useCurrentUser';
import {
  registerQueryClient,
  unregisterQueryClient,
} from '@/lib/reactQueryHelpers/activeQueryClient';
import { Refine } from '@refinedev/core';
import routerBindings from '@refinedev/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router';
import { accessControlProvider } from '../providers/accessControl';
import { authProvider } from '../providers/authProvider';
import {
  aiServiceDataProvider,
  blazeDataProvider,
  defaultDataProvider,
} from '../providers/dataProvider';
import { useAppResources } from './Resources';

// APL-11850: extracted verbatim from src/App.tsx. Everything shell-agnostic
// lives here so the v1 (antd) and v4 shells share one provider core
// (structural rule 1: view layer forks, everything else is shared).

// ── Notification bridge (Slice 5, spec § Forms & data — Notifications) ──────
// One Refine notificationProvider serves both shells; each shell registers its
// handler at module load: App.tsx the sonner one, AppV1.tsx the antd-message
// one (src/providers/apMessageNotificationProvider — v1 chunk only, so neither
// antd nor sonner enters the other shell's graph). Unregistered = no-op:
// registration always precedes the first Refine mutation. (IK1, 7.3)
type NotificationOpenParams = {
  type?: string;
  message?: string;
  description?: string;
  key?: string;
};

export type ShellNotificationHandler = {
  open: (params: NotificationOpenParams) => void;
  close: (key: string) => void;
};

let shellNotificationHandler: ShellNotificationHandler | null = null;

export const registerShellNotificationHandler = (handler: ShellNotificationHandler): void => {
  shellNotificationHandler = handler;
};

const bridgedNotificationProvider = () => ({
  open: (params: NotificationOpenParams) => shellNotificationHandler?.open(params),
  close: (key: string) => shellNotificationHandler?.close(key),
});

// Outer shared wrapper: router + scroll restoration + main config context.
export const AppProviders = ({ children }: { children: ReactNode }) => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* <RefineKbarProvider> */}
      <MainConfigContextProvider>{children}</MainConfigContextProvider>
      {/* </RefineKbarProvider> */}
    </BrowserRouter>
  );
};

// R2-G62 follow-up: expose the Refine-created QueryClient (fresh per <Refine
// key> mount) so authStore.logout() can clear it — with staleTime Infinity and
// the key held across logout→same-user relogin, that clear is the only
// freshness seam. Must sit inside <Refine> (that's the QueryClientProvider).
const ActiveQueryClientBridge = () => {
  const queryClient = useQueryClient();
  useEffect(() => {
    registerQueryClient(queryClient);
    return () => unregisterQueryClient(queryClient);
  }, [queryClient]);
  return null;
};

// Refine core config: data providers, i18n, notifications, router bindings,
// auth, access control, shared resources list, react-query defaults.
export const AppRefine = ({ children }: { children: ReactNode }) => {
  const i18nProvider = useI18nProvider();
  const currentUser = useCurrentUser();
  const { translate } = i18nProvider;
  const resources = useAppResources(translate);
  // Per-instance resolver (not module state): survives re-renders, resets with
  // the component — no HMR/test-order leakage (R2-G62 — see refineKey.ts).
  const resolveRefineKey = useRef<ReturnType<typeof createRefineKeyResolver> | null>(null);
  resolveRefineKey.current ??= createRefineKeyResolver();

  return (
    <Refine
      key={resolveRefineKey.current(currentUser?.id)} // Remount ONLY on a real user switch (R2-G62 — see refineKey.ts)
      dataProvider={{
        default: defaultDataProvider,
        aiService: aiServiceDataProvider,
        blaze: blazeDataProvider,
      }}
      i18nProvider={i18nProvider}
      notificationProvider={bridgedNotificationProvider}
      routerProvider={routerBindings}
      authProvider={authProvider}
      accessControlProvider={accessControlProvider}
      resources={resources}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
        disableTelemetry: true,
        useNewQueryKeys: true,
        reactQuery: {
          clientConfig: {
            defaultOptions: {
              queries: {
                staleTime: Infinity,
                retry: 1,
                networkMode: 'always', // Always attempt to refetch even if the browser reports offline.
              },
              mutations: {
                networkMode: 'always', // Ensure mutations aren't blocked by navigator.onLine checks.
              },
            },
          },
        },
        // projectId: "I0NThk-HGVdW5-tRoWLI",
      }}
    >
      <ActiveQueryClientBridge />
      {children}
    </Refine>
  );
};
